import { useMemo } from "react";
import { useQuery } from "./dataClient.js";
import { sumSeries } from "./renderers.js";
import { candidateDimsFor, computeLift } from "./compositionModel.js";
import { distinctivenessScore, rankDistinctiveness } from "./dimensionDistinctiveness.js";
import { encodeScopePath } from "./dimensionDefs.js";

// useScopeDistinctiveness — ranks the candidate next-drill dimensions for a scope
// by how much the scope's distribution over each diverges from the org baseline
// (see dimensionDistinctiveness.js). Drives BOTH the data-driven Composition
// column selection and the Position stack's "suggested next" rows.
//
// Fetches, per candidate dim, the scope's top-K breakdown and the org's — plus
// the scope + org prompt totals as denominators. Fixed-shape hooks (2 bundles +
// MAX_CANDIDATES × 2 entry queries) so the order never changes. The per-dim
// queries reuse the exact keys the composition/baseline fetch, so they dedupe
// through the query cache rather than doubling network. Disabled at the tenant
// scope, where "distinct vs the org" compares a scope to itself.

const MAX_CANDIDATES = 8;
const SCOPE_K = 8;  // matches ScopeComposition ROWS → shared cache entry
const ORG_K = 50;   // matches useScopeBaseline BASE_LIMIT → shared cache entry

/**
 * Score the fetched candidate-dim slots and rank them by distinctiveness. For
 * each dim: the JSD-based score vs the org, plus the "reason" — the member that
 * over-indexes hardest here (topMember/topLift). Pure — unit-tested in
 * useScopeDistinctiveness.test.js; the hook only wires the fixed-shape queries
 * and the enabled/loading guards around it.
 * @param {{slots:Array, scopeTotal:number, orgTotal:number}} args
 */
export function scoreCandidates({ slots, scopeTotal, orgTotal }) {
  const scored = [];
  for (const s of slots) {
    if (!s.dimKey) continue;
    const scopeEntries = s.scopeData?.entries || [];
    const orgEntries = s.orgData?.entries || [];
    const { score, jsd, topShare } = distinctivenessScore({ scopeEntries, scopeTotal, orgEntries, orgTotal });
    // The "reason": the member that over-indexes hardest here vs the org.
    const orgMap = new Map(orgEntries.map((e) => [String(e.id), Number(e.activity) || 0]));
    let topMember = null;
    let topMemberId = null;
    let topLift = 0;
    for (const e of scopeEntries) {
      const lift = computeLift({
        scopeActivity: Number(e.activity) || 0, scopeTotal,
        baseActivity: orgMap.get(String(e.id)) || 0, baseTotal: orgTotal,
      });
      if (lift != null && lift > topLift) { topLift = lift; topMember = e.label; topMemberId = String(e.id); }
    }
    scored.push({
      dimKey: s.dimKey, score, jsd, topShare, topMember, topMemberId, topLift,
      distinct: s.scopeData?.total ?? scopeEntries.length,
    });
  }
  return rankDistinctiveness(scored);
}

export function useScopeDistinctiveness(scopePath, range) {
  const path = scopePath || [];
  const enabled = path.length > 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const candidates = useMemo(() => (enabled ? candidateDimsFor(path, MAX_CANDIDATES) : []), [enabled, encodeScopePath(path)]);

  const { data: scopeBundle } = useQuery(
    "getOverviewActivityBundle", { scopePath: path, bucketCount: 1, topN: 1, range }, { enabled },
  );
  const scopeTotal = sumSeries(scopeBundle?.prompts?.total);
  const { data: orgBundle } = useQuery(
    "getOverviewActivityBundle", { scopePath: [], bucketCount: 1, topN: 1, range }, { enabled },
  );
  const orgTotal = sumSeries(orgBundle?.prompts?.total);

  const slots = [];
  for (let i = 0; i < MAX_CANDIDATES; i++) {
    const dimKey = candidates[i] || null;
    const on = enabled && Boolean(dimKey);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: scopeData } = useQuery(
      "getDimensionEntries", { dimKey: dimKey || "endpoints", parentScopePath: path, limit: SCOPE_K, range, sparkline: false }, { enabled: on },
    );
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: orgData } = useQuery(
      "getDimensionEntries", { dimKey: dimKey || "endpoints", parentScopePath: [], limit: ORG_K, range, sparkline: false }, { enabled: on },
    );
    slots.push({ dimKey, scopeData, orgData });
  }

  return useMemo(() => {
    if (!enabled) return { ranked: [], scopeTotal: 0, loading: false };
    const loading = !(scopeTotal > 0) || !(orgTotal > 0);
    return { ranked: scoreCandidates({ slots, scopeTotal, orgTotal }), scopeTotal, loading };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scopeTotal, orgTotal, ...slots.map((s) => s.scopeData), ...slots.map((s) => s.orgData)]);
}
